import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {PhoneInput, FlagImage, defaultCountries, parseCountry} from 'react-international-phone'
import 'react-international-phone/style.css'

/** Mobile = the portal's 576 line. */
const MOBILE_QUERY = '(max-width: 576px)'

const COUNTRIES = defaultCountries.map(parseCountry)

/**
 * `PhoneInput` whose country picker becomes a full-height sheet with search on mobile.
 *
 * Desktop keeps the library's own dropdown. On mobile the selector button's click is
 * caught in the CAPTURE phase (before the library's handler opens the dropdown) and our
 * sheet opens instead; a pick goes back through the input's own `setCountry`, so the
 * value and `onChange` behave exactly as if the dropdown had been used.
 */
export default function CountryPhoneInput(props) {
    const inputRef = useRef(null)
    const [open, setOpen] = useState(false)
    const close = useCallback(() => setOpen(false), [])

    const onClickCapture = (e) => {
        if (!e.target.closest('.react-international-phone-country-selector-button')) return
        if (!window.matchMedia(MOBILE_QUERY).matches) return
        e.preventDefault()
        e.stopPropagation()
        setOpen(true)
    }

    const select = (iso2) => {
        inputRef.current?.setCountry(iso2, {focusOnInput: true})
        setOpen(false)
    }

    return (
        <div onClickCapture={onClickCapture}>
            <PhoneInput ref={inputRef} {...props}/>
            {open && (
                <CountrySheet
                    selected={inputRef.current?.state?.country?.iso2}
                    onSelect={select}
                    onClose={close}
                />
            )}
        </div>
    )
}

function CountrySheet({selected, onSelect, onClose}) {
    const [query, setQuery] = useState('')

    const results = useMemo(() => {
        const q = query.trim().toLowerCase().replace(/^\+/, '')
        if (!q) return COUNTRIES
        return COUNTRIES.filter((c) =>
            c.name.toLowerCase().includes(q) || c.iso2 === q || c.dialCode.startsWith(q))
    }, [query])

    // Lock the page behind the sheet, and let Escape close it.
    useEffect(() => {
        const overflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = overflow
            document.removeEventListener('keydown', onKey)
        }
    }, [onClose])

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-end bg-black/50" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Select country"
                onClick={(e) => e.stopPropagation()}
                className="flex h-[85dvh] w-full animate-sheet-up flex-col rounded-t-(--radius-2xl) bg-(--bg-white) text-(--text-primary)"
            >
                <div className="flex items-center justify-between px-4 pt-5 pb-3">
                    <h2 className="text-lg font-semibold">Select country</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-(--bg-grey)"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                            <path d="M18 6 6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div className="px-4 pb-3">
                    <div className="flex h-12 items-center gap-2 rounded-full bg-(--bg-grey) px-4">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-(--text-secondary)" aria-hidden="true">
                            <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
                        </svg>
                        <input
                            type="search"
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search country or code"
                            className="h-full w-full bg-transparent text-base outline-none placeholder:text-(--text-secondary)"
                        />
                    </div>
                </div>

                <ul className="flex-1 overflow-y-auto overscroll-contain px-2 pb-6">
                    {results.map((c) => (
                        <li key={c.iso2}>
                            <button
                                type="button"
                                onClick={() => onSelect(c.iso2)}
                                className={`flex w-full items-center gap-3 rounded-(--radius-md) px-3 py-3 text-left ${
                                    c.iso2 === selected ? 'bg-(--bg-grey) font-semibold' : ''
                                }`}
                            >
                                <FlagImage iso2={c.iso2} size="24px"/>
                                <span className="flex-1 truncate">{c.name}</span>
                                <span className="text-sm text-(--text-secondary)">+{c.dialCode}</span>
                            </button>
                        </li>
                    ))}
                    {results.length === 0 && (
                        <li className="px-3 py-8 text-center text-sm text-(--text-secondary)">No country found</li>
                    )}
                </ul>
            </div>
        </div>,
        document.body,
    )
}
